<template>
  <div
    class="rights-feature rights-feature--credit_coin !justify-normal gap-[20px]"
  >
    <div class="flex-y-center justify-between text-14">
      <div class="flex gap-[20px]">
        <div
          v-for="item in tabMap"
          :key="item.key"
          class="flex-y-center text-font-1 cursor-pointer font-bold"
          :class="{
            'text-font-60 font-medium': activeKey !== item.key,
          }"
          @click="activeKey = item.key"
        >
          {{ item.name }}
          <a-popover v-if="item.key === 'all'" placement="bottom">
            <template #content>
              <span class="text-font-1">
                {{
                  $t('you-xian-xiao-hao-jie-jin-shi-xiao-shi-jian-de-yan-bi')
                }}
              </span>
            </template>
            <iconpark-icon
              class="ml-[5px] text-font-60 cursor-pointer"
              size="16"
              name="icon-circle-info"
            ></iconpark-icon>
          </a-popover>
        </div>
      </div>
      <div class="flex-y-center">
        <slot name="extra"></slot>
      </div>
    </div>
    <div class="h-[200px]">
      <div
        class="w-full h-full flex-y-center justify-center gap-[30px] relative"
      >
        <div class="flex flex-col gap-[20px]">
          <div
            v-for="(item, index) in chartData"
            :key="index"
            class="flex flex-wrap items-center text-12 min-w-[150px]"
          >
            <div class="flex-y-center gap-[5px]">
              <span
                class="w-[6px] h-[6px] rounded-full inline-block"
                :style="{
                  border: `3px solid` + item.color,
                }"
              ></span>

              <span class="text-font-60">
                {{ item.name }}
                {{ $t('colon') }}&nbsp;
              </span>
            </div>
            <span class="text-font-1">
              {{ item.remain }} / {{ item.total }}
            </span>
          </div>
          <div class="flex flex-wrap text-[12px] h-[13px] font-medium">
            <template v-if="activeKey !== 'all'">
              <span class="text-font-60">
                {{ $t('dang-qian-zong-sheng-yu') }}
                {{ $t('colon') }}&nbsp;
              </span>
              <div class="text-font-1">
                <span>{{ totalUsage.remain }}</span>
                <span>/{{ totalUsage.total }}</span>
              </div>
            </template>
          </div>
        </div>
        <ClientOnly>
          <CoinChart :data="chartData"></CoinChart>
        </ClientOnly>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts" name="FeatureCreditCoin">
import CoinChart from './chart/coin.vue'
import { ORDER_TYPE } from '~/api/interface/rights'
import { reqCreditCoinOrder, type ICreditCoinOrder } from '~/api'
import { useTime } from '~/hooks/useTime'

withDefaults(
  defineProps<{
    // usage?: {
    //   remain: number | string
    //   total: number | string
    //   percent: number
    // }
    disabled?: boolean
  }>(),
  {
    disabled: false,
  }
)

const colorMap: any = {
  99: '#6962FF', // 套餐剩余
  7: '#29E69C', // 活动剩余
  6: '#FFB53D', // 加购剩余
}

const { $t } = useNuxtApp()

const nameMap: any = {
  99: $t('tao-can-sheng-yu'),
  7: $t('huo-dong-sheng-yu'),
  6: $t('jia-gou-sheng-yu'),
}

function defaultUsageNumbers() {
  return {
    remain: 0,
    total: 0,
  }
}

const totalUsage = reactive(defaultUsageNumbers())
const activeKey = ref('current')
const chartData = ref()
const tabMap = [
  {
    key: 'current',
    name: $t('dang-qian-yan-bi'),
  },
  {
    key: 'all',
    name: $t('quan-bu-yan-bi'),
  },
]

// 统计单个类型
function calcNumbers(list: ICreditCoinOrder[]) {
  const stat = defaultUsageNumbers()
  list.forEach((order) => {
    if (order.status === 1) {
      stat.remain += order.remained_credit_coin || 0
    }

    stat.total += order.total_credit_coin || 0
  })
  return stat
}

// 统计所有类型数据
function statNumbers(data: any) {
  const usage_package = calcNumbers(
    data.filter((record: any) => record.order_type === ORDER_TYPE.PACKAGE)
  )
  const usage_activity = calcNumbers(
    data.filter((record: any) => record.order_type === ORDER_TYPE.ACTIVITY)
  )
  const usage_credits = calcNumbers(
    data.filter((record: any) => record.order_type === ORDER_TYPE.CREDITS)
  )

  totalUsage.remain =
    usage_package.remain + usage_activity.remain + usage_credits.remain
  totalUsage.total =
    usage_package.total + usage_activity.total + usage_credits.total

  return {
    [ORDER_TYPE.PACKAGE]: usage_package,
    [ORDER_TYPE.ACTIVITY]: usage_activity,
    [ORDER_TYPE.CREDITS]: usage_credits,
  }
}

const userInfo = useUserInfo()

const { timeFormat } = useTime()

const expiredTime = timeFormat(userInfo.value?.expired_time, 'YYYY-MM-DD')

async function fetchData(ordering_expired_time = 0) {
  const { code, data } = await reqCreditCoinOrder({
    check_all: activeKey.value === 'current' ? 0 : 1,
    ordering_expired_time,
  })
  if (!code && data) {
    const total: any = statNumbers(data.order_list)
    chartData.value = Object.keys(total)?.map((item) => ({
      color: colorMap[item],
      remain: total[item].remain,
      total: total[item].total,
      percent: total[item].remain
        ? Number((total[item].remain / total[item].total).toFixed(2))
        : 0,
      name: nameMap[item],
      expired:
        Number(item) === 99 ? `${$t('shi-xiao-shi-jian')}：${expiredTime}` : '',
    }))
  }
}

onMounted(() => {
  emitter.on(EVENT.UPDATE_USER, () => {
    fetchData()
  })
})

watch(
  () => activeKey.value,
  () => fetchData(),
  { immediate: true }
)
</script>
